import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import commaNumber from 'comma-number';
import gsap from 'gsap';

export const useCountUp = ({ from = 0, to, duration = .3, ease = 'power3.out', useCommas = true, updateFrom = true }) => {
  const [tweenedValue, setTweenedValue] = useState(from);
  const [newFrom, setNewFrom] = useState(null);
  const obj = useRef({ val: from });
  const tween = useRef(null);

  useEffect(() => {
    obj.current.val = !!newFrom && !!updateFrom ? newFrom : from;
    if (tween.current) tween.current.kill();
    tween.current = gsap.to(obj.current, {
      duration: duration,
      val: to,
      ease: ease,
      onUpdate: () => {
        let number = Math.ceil(obj.current.val);
        if (useCommas) number = commaNumber(number);
        setTweenedValue(number);
      }
    });
    setNewFrom(to);

    return () => {
      tween.current.kill();
    }
  }, [to]);

  return tweenedValue;
}

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const useEventListener = (eventName, handler, element = window) => {
  // Create a ref that stores handler
  const savedHandler = useRef();

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On 
      const isSupported = element && element.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref
      const eventListener = event => savedHandler.current(event);

      // Add event listener
      element.addEventListener(eventName, eventListener);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener);
      };
    },
    [eventName, element] // Re-run if eventName or element changes
  );
};